import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { Price } from '../types/models/Price';
import type { ApiResponsePriceList } from '../types/api/ApiResponsePrice';

interface UsePricesReturn {
  prices: Price[];
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
  /**
   * Replace one product's row in place from a PUT/DELETE /prices/{slug} response. The single-price
   * routes return the now-effective Price, so an edit/reset updates its row without a full refetch
   * (which would flash the loading spinner over the table).
   */
  applyPrice: (updated: Price) => void;
}

// GET /prices returns the in-scope organization's effective price per product (platform defaults
// for a clientless caller). useApiFetch injects the bearer token and the impersonation header, so
// the list reflects the impersonated client with no extra work here.
//
// `enabled` (default true) defers the fetch: a consumer that only needs prices conditionally — e.g.
// a modal passing `isOpen` — avoids hitting /prices until it actually opens, and refetches each time
// it re-enables.
export const usePrices = (enabled = true): UsePricesReturn => {
  const [prices, setPrices] = useState<Price[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchPrices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/prices');
      // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller
      // exceptions to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
      const body = (await response.json().catch(() => null)) as ApiResponsePriceList | null;
      if (body?.status === 'success' && body.data?.prices) {
        setPrices(body.data.prices);
      } else {
        setError(body?.message ?? 'Failed to load prices');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching prices');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  const applyPrice = useCallback((updated: Price) => {
    setPrices((prev) => prev.map((p) => (p.product_key === updated.product_key ? updated : p)));
  }, []);

  useEffect(() => {
    if (auth.isAuthenticated && enabled) {
      void fetchPrices();
    }
  }, [auth.isAuthenticated, enabled, fetchPrices]);

  return { prices, loading, error, refetch: fetchPrices, applyPrice };
};
